import { CoerceBoolean, EAppNameEnum, TAppNameEnum } from '@onesource/schemas';
import { EFeatureFlagEnum, TFeatureFlagKey } from './hooks/use-feature-flag-utils';
import { object, string } from 'zod';

const envSchema = object({
  VITE_BASE_API_ENDPOINT: string().url(),
  VITE_CDN_BASE_URL: string().url(),
  VITE_SHOW_DEV_TOOLS: CoerceBoolean,
  VITE_FIT_FRONTEND_URL: string().url(),
  VITE_RYC_FRONTEND_URL: string().url(),
  VITE_ADDON_FE_URL: string().url(),
  VITE_WP_MEMBERS_SITE: string().url(),
  VITE_NOVU_APPLICATION_IDENTIFIER: string(),
  VITE_GA4_MEASUREMENT_ID: string().optional(),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  throw new Error(
    `Missing required environment variables:\n${parsed.error.issues.map(i => `  - ${i.path.join('.')}: ${i.message}`).join('\n')}`,
  );
}

const env = parsed.data;

export const SHOW_DEV_TOOLS = env.VITE_SHOW_DEV_TOOLS;

export const API_BASE_URL = env.VITE_BASE_API_ENDPOINT;
export const CDN_BASE_URL = env.VITE_CDN_BASE_URL;

// App URLs for SSO
export const FIT_FRONTEND_URL = env.VITE_FIT_FRONTEND_URL;
export const RYC_FRONTEND_URL = env.VITE_RYC_FRONTEND_URL;
export const ADDON_FRONTEND_URL = env.VITE_ADDON_FE_URL;

export const WP_MEMBERS_SITE = env.VITE_WP_MEMBERS_SITE;
export const NOVU_APPLICATION_IDENTIFIER = env.VITE_NOVU_APPLICATION_IDENTIFIER;

export const GA4_MEASUREMENT_ID = env.VITE_GA4_MEASUREMENT_ID;

// App configuration with subscription keys
export interface TAppConfig {
  id: TAppNameEnum;
  name: {
    subscribed: string;
    unsubscribed: string;
  };
  description: string;
  url: string | undefined;
  icon: string;
  thumbnails: {
    default: string;
    TIER_1?: string;
    TIER_2?: string;
    TIER_3?: string;
  };
  tierLabels?: {
    TIER_1?: string;
    TIER_2?: string;
    TIER_3?: string;
  };
  featureFlagKey?: TFeatureFlagKey;
  salesPageUrl: string | undefined;
}

export const FIT_PRODUCT_ID = 21739;
export const FIT_PRODUCT_URL = 'https://members.laurenohayon.com/product/ryc-fit-yearly';
export const CHANGE_FIT_PLAN_URL = 'https://members.laurenohayon.com/fit-change-plan';

// All available apps CONFIGURATION
export const ALL_APPS: TAppConfig[] = [
  {
    id: EAppNameEnum.RYC,
    name: {
      subscribed: 'RYC<sup>®</sup>',
      unsubscribed: 'RYC<sup>®</sup> 12-Week Program',
    },
    description: 'Rock Your Core 12 Week Challenge',
    url: RYC_FRONTEND_URL,
    icon: 'mdi:yoga',
    tierLabels: {
      TIER_1: 'Essentials',
      TIER_2: 'Guided',
      TIER_3: 'Coached',
    },
    thumbnails: {
      default: 'https://media.restoreyourcore.com/members/tier-1-essentials.webp',
      TIER_1: 'https://media.restoreyourcore.com/members/tier-1-essentials.webp',
      TIER_2: 'https://media.restoreyourcore.com/members/tier-2-guided.webp',
      TIER_3: 'https://media.restoreyourcore.com/members/tier-3-coached.webp',
    },
    salesPageUrl: 'https://restoreyourcore.com/restore-your-core/',
  },
  {
    id: EAppNameEnum.FIT,
    name: {
      subscribed: 'RYC<sup>®</sup> FIT',
      unsubscribed: 'RYC<sup>®</sup> FIT',
    },
    description: 'Access your fitness classes and workouts',
    url: FIT_FRONTEND_URL,
    icon: 'mdi:dumbbell',
    thumbnails: {
      default: 'https://media.restoreyourcore.com/members/ryc-fit.webp',
    },
    salesPageUrl: 'https://restoreyourcore.com/fitness-membership/',
  },
  {
    id: EAppNameEnum.ADDON,
    name: {
      subscribed: 'Addon',
      unsubscribed: 'Addon',
    },
    description: 'Access your addon programs and content',
    url: ADDON_FRONTEND_URL,
    icon: 'mdi:puzzle',
    featureFlagKey: EFeatureFlagEnum.SHOW_ADDON_APP,
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
  {
    id: EAppNameEnum.SHOULDERS_MELT_MOBILIZE,
    name: {
      subscribed: 'Shoulders: Melt + Mobilize',
      unsubscribed: 'Shoulders: Melt + Mobilize',
    },
    description: 'Shoulder mobility and relaxation program',
    url: undefined,
    icon: 'mdi:arm-flex',
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
  {
    id: EAppNameEnum.RYC_DOWNLOADABLE,
    name: {
      subscribed: 'RYC Downloadable',
      unsubscribed: 'RYC Downloadable',
    },
    description: 'Downloadable RYC content',
    url: undefined,
    icon: 'mdi:download',
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
  {
    id: EAppNameEnum.ROOTED_RECOVERY,
    name: {
      subscribed: 'Rooted Recovery',
      unsubscribed: 'Rooted Recovery',
    },
    description: 'Recovery and healing program',
    url: undefined,
    icon: 'mdi:leaf',
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
  {
    id: EAppNameEnum.OPEN_CLINIC,
    name: {
      subscribed: 'Open Clinic',
      unsubscribed: 'Open Clinic',
    },
    description: 'Open clinic sessions',
    url: undefined,
    icon: 'mdi:hospital-building',
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
  {
    id: EAppNameEnum.CHALLENGE_YOUR_CORE,
    name: {
      subscribed: 'Challenge Your Core',
      unsubscribed: 'Challenge Your Core',
    },
    description: 'Core challenge program',
    url: undefined,
    icon: 'mdi:trophy',
    thumbnails: {
      default: '',
    },
    salesPageUrl: undefined,
  },
] as const;

// Legacy export for backward compatibility - apps with URLs configured
export const AVAILABLE_APPS = ALL_APPS.filter(app => app.url);
export type TAvailableApp = typeof ALL_APPS;
